import React, { Component } from 'react';
import { connect } from "react-redux";
import * as actions from "../actions";
import { profileRef, skillsRef, experienceRef, educationRef, achievementsRef } from '../config/firebase';
import { onValue } from 'firebase/database';
import { supabase, PROFILE_BUCKET } from '../config/supabase';
import { Link } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { FaUpload } from 'react-icons/fa';
import { LuBriefcase, LuGraduationCap, LuAward, LuMedal, LuStar, LuUser, LuX, LuMaximize2, LuPencilLine, LuSave, LuSparkles } from 'react-icons/lu';
import { getCroppedImg, dataUrlToBlob, extensionForMime } from '../actions/cropUtils';
import { showToast } from '../actions/toastBus';
import '../stylesheet/Home.css';
import '../stylesheet/style.css';

const ACHIEVEMENT_ICONS = {
    Award: LuAward,
    Competition: LuMedal,
    Academic: LuGraduationCap
};

// Same loose date parsing as Experience.jsx, kept local since it's only
// needed here to total up a career span across all roles.
const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

const isCurrentRole = (end) => !end || /present|current/i.test(end);

const parseMonthYear = (str) => {
    if (!str) return null;
    const s = str.trim().toLowerCase();
    const monthYear = s.match(/([a-z]{3,9})\.?\s+(\d{4})/);
    if (monthYear) {
        const idx = MONTHS.findIndex((m) => monthYear[1].startsWith(m));
        if (idx !== -1) return { y: parseInt(monthYear[2], 10), m: idx };
    }
    const yearOnly = s.match(/^(\d{4})$/);
    if (yearOnly) return { y: parseInt(yearOnly[1], 10), m: 0 };
    return null;
};

// Calendar-aware "time since" breakdown (same borrow logic as an age
// calculator) so the live experience timer counts up smoothly second by
// second instead of just showing a static total.
const getTimeBreakdown = (start, now) => {
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();
    let hours = now.getHours() - start.getHours();
    let minutes = now.getMinutes() - start.getMinutes();
    let seconds = now.getSeconds() - start.getSeconds();

    if (seconds < 0) { seconds += 60; minutes -= 1; }
    if (minutes < 0) { minutes += 60; hours -= 1; }
    if (hours < 0) { hours += 24; days -= 1; }
    if (days < 0) {
        days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        months -= 1;
    }
    if (months < 0) { months += 12; years -= 1; }
    if (years < 0) return null;
    return { years, months, days, hours, minutes, seconds };
};

const pad2 = (n) => String(n).padStart(2, '0');

class Home extends Component {
    constructor(props) {
        super(props);
        this.state = {
            home: '',
            skills: [],
            experiences: [],
            education: [],
            achievements: [],
            loading: true,
            isLoggedIn: false,
            showUpload: false,
            cropModalOpen: false,
            uploadedImage: null,
            uploadedImageType: null,
            croppedAreaPixels: null,
            crop: { x: 0, y: 0 },
            zoom: 1,
            profileModalOpen: false,
            profileModalClosing: false,
            // Separate popup for Name + Description - kept in its own draft
            // fields so opening it never touches the live `home` values
            // until Save is actually pressed.
            profileEditOpen: false,
            profileEditClosing: false,
            draftName: '',
            draftDetails: '',
            now: new Date()
        };
    };

    imageLoaded = () => {
        this.setState({ loading: false });
    }

    openProfileModal = () => {
        this.setState({ profileModalOpen: true, profileModalClosing: false });
    };

    closeProfileModal = () => {
        // Play a quick exit transition before actually unmounting the
        // modal, instead of just yanking it away - the exit should read
        // as fast/light while the entrance reads as fuller/deliberate.
        this.setState({ profileModalClosing: true });
        window.clearTimeout(this._profileModalTimer);
        this._profileModalTimer = window.setTimeout(() => {
            this.setState({ profileModalOpen: false, profileModalClosing: false });
        }, 320);
    };

    handleProfileModalKey = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openProfileModal();
        }
    };

    // Name + description popup - independent of the "view profile" modal
    // above and of the photo upload below, so editing one never blocks or
    // silently piggybacks on the other.
    openProfileEditModal = () => {
        this.setState({
            profileEditOpen: true,
            profileEditClosing: false,
            draftName: this.state.home.name || '',
            draftDetails: this.state.home.details || ''
        });
    };

    closeProfileEditModal = () => {
        this.setState({ profileEditClosing: true });
        window.clearTimeout(this._profileEditModalTimer);
        this._profileEditModalTimer = window.setTimeout(() => {
            this.setState({ profileEditOpen: false, profileEditClosing: false });
        }, 320);
    };

    handleProfileEditChange = (field, value) => {
        this.setState({ [field]: value });
    };

    saveProfileDetails = async () => {
        const { draftName, draftDetails } = this.state;
        if (!draftName.trim()) return showToast('Name is required.', 'error');

        const { updateProfile } = this.props;
        try {
            await updateProfile({
                name: draftName,
                details: draftDetails,
                image: this.state.home.image
            });
            this.setState((prev) => ({
                home: { ...prev.home, name: draftName, details: draftDetails }
            }));
            this.closeProfileEditModal();
            showToast('Profile updated', 'success');
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast('Could not update your profile - please try again.', 'error');
        }
    };

    handleKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        if (this.state.profileEditOpen) {
            this.closeProfileEditModal();
        } else if (this.state.profileModalOpen) {
            this.closeProfileModal();
        }
    };

    openCropModal = () => {
        this.fileInput.click();
    };

    handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
            this.setState({
                uploadedImage: reader.result,
                // Kept so the crop/upload below can output the same
                // format instead of forcing everything through JPEG.
                uploadedImageType: file.type || 'image/jpeg',
                cropModalOpen: true,
                croppedAreaPixels: null, // reset crop area
                crop: { x: 0, y: 0 },
                zoom: 1
            });
            };
            reader.readAsDataURL(file);
        }

        // Allow re-uploading same file
        event.target.value = '';
    };

    onCropComplete = (_, croppedAreaPixels) => {
        this.setState({ croppedAreaPixels });
    };

    // Photo saves itself right after cropping - independent of the Name/
    // Description popup's own Save. Shows the crop immediately (instant
    // local preview), then swaps in the hosted URL once the upload
    // finishes instead of leaving the change unpersisted until some other
    // button gets clicked.
    handleCropSave = async () => {
        const mimeType = this.state.uploadedImageType || 'image/jpeg';
        const croppedImage = await getCroppedImg(
            this.state.uploadedImage,
            this.state.croppedAreaPixels,
            mimeType
        );

        this.setState((prev) => ({
            home: { ...prev.home, image: croppedImage },
            cropModalOpen: false,
            uploadedImage: null,
            croppedAreaPixels: null
        }));

        try {
            const blob = dataUrlToBlob(croppedImage);
            const fileName = `profile-${Date.now()}.${extensionForMime(blob.type)}`;

            const { error: uploadError } = await supabase.storage
                .from(PROFILE_BUCKET)
                .upload(fileName, blob, { contentType: blob.type, upsert: true });
            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from(PROFILE_BUCKET).getPublicUrl(fileName);
            const downloadURL = data.publicUrl;

            const { updateProfile } = this.props;
            await updateProfile({
                name: this.state.home.name,
                details: this.state.home.details,
                image: downloadURL
            });

            this.setState((prev) => ({ home: { ...prev.home, image: downloadURL } }));
        } catch (error) {
            console.error('Error saving profile photo:', error);
            // Surface the real reason (usually a missing Storage RLS
            // policy on first setup) instead of a generic message that
            // hides what actually needs fixing.
            showToast(`Could not save the new photo: ${error.message || 'please try again.'}`, 'error');
        }
    };

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        // Ticks the "years / months / days · hh:mm:ss" experience timer
        // live, once a second.
        this.experienceTimerInterval = window.setInterval(() => {
            this.setState({ now: new Date() });
        }, 1000);
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(profileRef, (snapshot) => {
            let homeData = snapshot.val();
            this.setState({
                home: homeData
            });
        });
        onValue(skillsRef, (snapshot) => {
            this.setState({ skills: snapshot.val() || [] });
        });
        onValue(experienceRef, (snapshot) => {
            this.setState({ experiences: snapshot.val() || [] });
        });
        onValue(educationRef, (snapshot) => {
            this.setState({ education: snapshot.val() || [] });
        });
        onValue(achievementsRef, (snapshot) => {
            const data = snapshot.val();
            const achievements = Array.isArray(data)
                ? data
                : (data ? Object.values(data) : []);
            this.setState({ achievements });
        });
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._profileModalTimer);
        window.clearTimeout(this._profileEditModalTimer);
        window.clearInterval(this.experienceTimerInterval);
    }

    getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good Morning';
        if (hour < 14) return 'Good Afternoon';
        if (hour < 20) return 'Good Evening';
        return 'Good Night';
    };

    getTopSkills = () => {
        const skills = Array.isArray(this.state.skills) ? this.state.skills : [];
        return [...skills]
            .filter((skill) => skill && typeof skill.percentage === 'number')
            .sort((a, b) => b.percentage - a.percentage)
            .slice(0, 6);
    };

    getCurrentJob = () => {
        const experiences = Array.isArray(this.state.experiences) ? this.state.experiences : [];
        if (!experiences.length) return null;
        const current = experiences.find((exp) => exp && (!exp.end || /present|current/i.test(exp.end)));
        return current || experiences[experiences.length - 1];
    };

    // Total actually-worked time, not just the calendar span from the
    // first role's start to the last role's end - that span-only version
    // silently counted any gap between jobs (e.g. a break, or education
    // in between) as "experience" too. This merges overlapping/adjacent
    // stints (so concurrent or back-to-back roles aren't double counted)
    // and only sums the months actually covered by a role.
    getTotalExperience = () => {
        const experiences = Array.isArray(this.state.experiences) ? this.state.experiences : [];
        const now = new Date();
        const nowIdx = now.getFullYear() * 12 + now.getMonth();

        const intervals = experiences
            .filter(Boolean)
            .map((exp) => {
                const start = parseMonthYear(exp.start);
                if (!start) return null;
                const startIdx = start.y * 12 + start.m;
                const end = isCurrentRole(exp.end) ? null : parseMonthYear(exp.end);
                const endIdx = isCurrentRole(exp.end) ? nowIdx : (end ? end.y * 12 + end.m : null);
                if (endIdx == null || endIdx < startIdx) return null;
                return [startIdx, endIdx];
            })
            .filter(Boolean)
            .sort((a, b) => a[0] - b[0]);

        if (!intervals.length) return null;

        let totalMonths = 0;
        let [curStart, curEnd] = intervals[0];
        for (let i = 1; i < intervals.length; i++) {
            const [s, e] = intervals[i];
            if (s <= curEnd) {
                curEnd = Math.max(curEnd, e);
            } else {
                totalMonths += curEnd - curStart;
                curStart = s;
                curEnd = e;
            }
        }
        totalMonths += curEnd - curStart;

        if (totalMonths <= 0) return null;
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        if (years < 1) return `${months} mo${months !== 1 ? 's' : ''}`;
        // Round up on 6+ leftover months so it reads as a clean "X+ yrs".
        return `${months >= 6 ? years + 1 : years}+ yrs`;
    };

    // Real Date for the earliest role's start (first of that month, since
    // only month/year is stored) - feeds the live-ticking timer below the
    // greeting, as opposed to getTotalExperience's rounded "X+ yrs" label.
    getEarliestExperienceStart = () => {
        const experiences = Array.isArray(this.state.experiences) ? this.state.experiences : [];
        let earliest = null;
        experiences.forEach((exp) => {
            if (!exp) return;
            const start = parseMonthYear(exp.start);
            if (start && (!earliest || start.y * 12 + start.m < earliest.y * 12 + earliest.m)) {
                earliest = start;
            }
        });
        return earliest ? new Date(earliest.y, earliest.m, 1) : null;
    };

    getHighestDegree = () => {
        const education = Array.isArray(this.state.education) ? this.state.education : [];
        if (!education.length) return null;
        return education.reduce((highest, edu) => {
            const year = parseInt(edu?.passout, 10) || 0;
            const highestYear = parseInt(highest?.passout, 10) || 0;
            return year >= highestYear ? edu : highest;
        }, education[0]);
    };

    getRecentAchievements = () => {
        const achievements = Array.isArray(this.state.achievements) ? this.state.achievements : [];
        // Entries are appended at the end (index = old length), so the
        // most recently added ones are the last items, not the first.
        return achievements.filter(Boolean).slice(-4).reverse();
    };

    render() {
        const greeting = this.getGreeting();
        const topSkills = this.getTopSkills();
        const maxPercentage = topSkills.reduce((max, skill) => Math.max(max, skill.percentage), 0) || 100;
        const currentJob = this.getCurrentJob();
        const highestDegree = this.getHighestDegree();
        const totalExperience = this.getTotalExperience();
        const recentAchievements = this.getRecentAchievements();
        const experienceStart = this.getEarliestExperienceStart();
        const experienceBreakdown = experienceStart ? getTimeBreakdown(experienceStart, this.state.now) : null;

        return (
            <div className='base'>
                <div className="home-hero">
                    <div className="hero-greeting">
                        <p className="greeting-line">
                            {greeting}{' '}<span className="greeting-name">{this.state.home.name}</span>
                        </p>
                        <h1 className="hero-tagline">{currentJob ? currentJob.title : 'Welcome to my portfolio'}</h1>
                        {experienceBreakdown && (
                            <div className="experience-timer" role="timer" aria-live="off">
                                <span className="experience-timer-dot" aria-hidden="true"></span>
                                <span className="experience-timer-label">Experience gained</span>
                                <div className="experience-timer-units">
                                    <span className="experience-timer-unit">
                                        <strong>{experienceBreakdown.years}</strong><small>{experienceBreakdown.years === 1 ? 'yr' : 'yrs'}</small>
                                    </span>
                                    <span className="experience-timer-unit">
                                        <strong>{experienceBreakdown.months}</strong><small>{experienceBreakdown.months === 1 ? 'mo' : 'mos'}</small>
                                    </span>
                                    <span className="experience-timer-unit">
                                        <strong>{experienceBreakdown.days}</strong><small>{experienceBreakdown.days === 1 ? 'day' : 'days'}</small>
                                    </span>
                                    <span className="experience-timer-clock">
                                        {pad2(experienceBreakdown.hours)}:{pad2(experienceBreakdown.minutes)}:{pad2(experienceBreakdown.seconds)}
                                    </span>
                                </div>
                            </div>
                        )}
                        {this.state.isLoggedIn && (
                            <button
                                type="button"
                                className="profile-edit-trigger"
                                onClick={this.openProfileEditModal}
                            >
                                <LuPencilLine/> Edit name &amp; description
                            </button>
                        )}
                    </div>
                    <div className="profile-card"
                        onMouseEnter={() => !this.state.loading && this.setState({ showUpload: true })}
                        onMouseLeave={() => this.setState({ showUpload: false })}>
                        <div className={`profile-skeleton${this.state.loading ? '' : ' is-hidden'}`} aria-hidden="true">
                            <span className="profile-skeleton-shimmer"></span>
                            <span className="profile-skeleton-avatar"><LuUser/></span>
                        </div>
                        <div
                            className={`profile-image-wrap${this.state.loading ? '' : ' is-visible'}`}
                            onClick={() => !this.state.loading && this.openProfileModal()}
                            onKeyDown={this.handleProfileModalKey}
                            role="button"
                            tabIndex={0}
                            aria-label="View full profile"
                        >
                            <img src={this.state.home.image} alt={this.state.home.name} onLoad={this.imageLoaded}/>
                        </div>
                        {this.state.isLoggedIn && this.state.showUpload && (
                            <div className="upload-overlay" onClick={this.openCropModal}>
                            <FaUpload className="upload-icon" />
                            </div>
                        )}
                        {!this.state.isLoggedIn && !this.state.loading && (
                            <div className="profile-view-hint" aria-hidden="true">
                                <span className="profile-view-hint-icon"><LuMaximize2/></span>
                            </div>
                        )}
                    </div>

                    <div
                        className="bio-card is-clickable"
                        onClick={this.openProfileModal}
                        onKeyDown={this.handleProfileModalKey}
                        role="button"
                        tabIndex={0}
                        aria-label="View full profile"
                    >
                        <p>{this.state.home.details}</p>
                    </div>
                </div>

                <div className="stats-widgets">
                    {topSkills.length > 0 && (
                        <Link className="stat-card" to="/skills">
                            <div className="stat-card-top">
                                <span className="stat-icon"><LuStar/></span>
                            </div>
                            <span className="stat-eyebrow">Top Skills</span>
                            <p>Where most of my hours go</p>
                            <div className="bars-row">
                                {topSkills.map((skill, i) => (
                                    <div className="bar-item" key={skill.name || i}>
                                        <span className="bar-value">{skill.percentage}%</span>
                                        <span className="bar-track">
                                            <span
                                                className={
                                                    skill.percentage === maxPercentage
                                                        ? 'bar-fill bar-fill-top'
                                                        : 'bar-fill'
                                                }
                                                style={{ height: `${Math.max(6, (skill.percentage / maxPercentage) * 100)}%` }}
                                            />
                                        </span>
                                        <span className="bar-label">{skill.name}</span>
                                    </div>
                                ))}
                            </div>
                        </Link>
                    )}

                    {currentJob && (
                        <Link className="stat-card" to="/experience">
                            <div className="stat-card-top">
                                <span className="stat-icon">
                                    {currentJob.cname
                                        ? <img src={currentJob.cname} alt={currentJob.title} />
                                        : <LuBriefcase/>}
                                </span>
                                <span className="stat-badge">Currently working</span>
                            </div>
                            <span className="stat-eyebrow">Present Company</span>
                            <h3>{currentJob.company}</h3>
                            <p>{currentJob.title}</p>
                            <span className="stat-meta">{currentJob.location}{currentJob.start ? ` · Since ${currentJob.start}` : ''}</span>
                        </Link>
                    )}

                    {highestDegree && (
                        <Link className="stat-card" to="/education">
                            <div className="stat-card-top">
                                <span className="stat-icon"><LuGraduationCap/></span>
                            </div>
                            <span className="stat-eyebrow">Highest Degree</span>
                            <h3>{highestDegree.name}</h3>
                            <p>{highestDegree.desc}</p>
                            <span className="stat-meta">
                                {highestDegree.passout ? `Passout ${highestDegree.passout}` : ''}
                                {highestDegree.percentage ? ` · ${highestDegree.percentage}` : ''}
                            </span>
                        </Link>
                    )}

                    {recentAchievements.length > 0 && (
                        <Link className="stat-card" to="/achievements">
                            <div className="stat-card-top">
                                <span className="stat-icon"><LuAward/></span>
                            </div>
                            <span className="stat-eyebrow">Recent Achievements</span>
                            <div className="achievement-mini-grid">
                                {recentAchievements.map((achievement, i) => {
                                    const Icon = ACHIEVEMENT_ICONS[achievement.tag] || LuAward;
                                    return (
                                        <div className="achievement-mini-card" key={`${achievement.title}-${i}`}>
                                            <span className="achievement-mini-icon"><Icon/></span>
                                            <span className="achievement-mini-title">{achievement.title}</span>
                                            <span className="achievement-mini-meta">{achievement.date}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </Link>
                    )}
                </div>

                {this.state.cropModalOpen && (
                <div className="crop-modal-backdrop" onClick={() => this.setState({ cropModalOpen: false })}>
                <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
                    <span className="crop-modal-eyebrow">Adjust photo</span>
                    <div className="cropper-wrapper">
                    <Cropper
                        image={this.state.uploadedImage}
                        crop={this.state.crop}
                        zoom={this.state.zoom}
                        aspect={1}
                        cropShape="round" // or "round"
                        showGrid={true}
                        onCropChange={(crop) => this.setState({ crop })}
                        onZoomChange={(zoom) => this.setState({ zoom })}
                        onCropComplete={this.onCropComplete}
                    />
                    </div>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={this.state.zoom}
                        onChange={(e) => this.setState({ zoom: parseFloat(e.target.value) })}
                        className="zoom-slider"
                    />
                    <div className="crop-actions">
                    <button onClick={this.handleCropSave}>Save crop</button>
                    <button onClick={() => this.setState({ cropModalOpen: false })}>Cancel</button>
                    </div>
                </div>
                </div>
                )}

                {this.state.profileModalOpen && (
                    <div
                        className={`profile-modal-backdrop${this.state.profileModalClosing ? ' is-closing' : ''}`}
                        onClick={this.closeProfileModal}
                    >
                        <div className="profile-modal-panel" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="profile-modal-close"
                                onClick={this.closeProfileModal}
                                aria-label="Close profile"
                            >
                                <LuX/>
                            </button>
                            <div className="profile-modal-media">
                                <img src={this.state.home.image} alt={this.state.home.name}/>
                                {currentJob && (
                                    <span className="profile-modal-chip"><LuBriefcase/> {currentJob.title}</span>
                                )}
                            </div>
                            <div className="profile-modal-content">
                                <span className="profile-modal-eyebrow">{greeting}, I'm</span>
                                <h2 className="profile-modal-name">{this.state.home.name}</h2>
                                {(currentJob || totalExperience) && (
                                    <p className="profile-modal-tagline">
                                        {totalExperience ? `${totalExperience} experience` : ''}
                                    </p>
                                )}
                                <p className="profile-modal-detail">{this.state.home.details}</p>
                                <div className="profile-modal-meta-row">
                                    {currentJob && (
                                        <span className="profile-modal-meta-chip">
                                            <LuBriefcase/>
                                            <span>{currentJob.company}</span>
                                        </span>
                                    )}
                                    {highestDegree && (
                                        <span className="profile-modal-meta-chip">
                                            <LuGraduationCap/>
                                            <span>{highestDegree.name}{highestDegree.passout ? ` · ${highestDegree.passout}` : ''}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.profileEditOpen && (
                    <div
                        className={`profile-edit-modal-backdrop${this.state.profileEditClosing ? ' is-closing' : ''}`}
                        onClick={this.closeProfileEditModal}
                    >
                        <div className="profile-edit-modal-panel" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="profile-edit-modal-close"
                                onClick={this.closeProfileEditModal}
                                aria-label="Close"
                            >
                                <LuX/>
                            </button>
                            <span className="profile-edit-eyebrow"><LuSparkles/> Admin</span>
                            <h3 className="profile-edit-heading">Edit name &amp; description</h3>
                            <label className="profile-edit-field">
                                <span className="profile-edit-field-label">Name</span>
                                <input
                                    type="text"
                                    value={this.state.draftName}
                                    onChange={(e) => this.handleProfileEditChange('draftName', e.target.value)}
                                    className="profile-edit-field-input"
                                    autoFocus
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span className="profile-edit-field-label">Description</span>
                                <textarea
                                    rows={5}
                                    value={this.state.draftDetails}
                                    onChange={(e) => this.handleProfileEditChange('draftDetails', e.target.value)}
                                    className="profile-edit-field-input profile-edit-field-textarea"
                                />
                            </label>
                            <div className="profile-edit-modal-actions">
                                <button
                                    type="button"
                                    onClick={this.saveProfileDetails}
                                    className="app-form-button profile-edit-save-cta"
                                >
                                    <LuSave/> Save changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={(ref) => (this.fileInput = ref)}
                    onChange={this.handleFileChange}
                />
            </div>
        )
    }
}

const mapStateToProps = ({data}) => {
    return {
        data
    }
}

export default connect(mapStateToProps, actions)(Home);
